// @ts-strict-ignore
import { call, put, select, take } from 'redux-saga/effects'
import * as BrownfieldBridgeInternal from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { LoginTypes } from 'APP/Redux/LoginRedux'
import { logger } from 'APP/Lib/Datadog'
import { login, loginState } from 'APP/Sagas/AuthSagas'
import { fetchPatientProfile, fetchUserInfo } from 'APP/Sagas/PatientSagas'
import { featuresMonitor } from 'APP/Sagas/FeaturesSaga'
import {
  patientProfileState,
  REGISTER_PUSH_TOKEN_RETRY_LIMIT,
  registerPushToken,
  registerPushVoipToken,
} from 'APP/Sagas/StartupSagas'
import { isIOS } from 'APP/Helpers/checkPlatform'
import { classifyStartupFailure } from 'APP/Helpers/StartupFailures'
import { initializationActions } from 'APP/Store/Initialization'
import { preloadActions } from './slice'

function* registerPreloadPushTokens() {
  const pushToken: string | null = yield call(BrownfieldBridgeInternal.getPushToken)
  if (pushToken) {
    try {
      yield call(registerPushToken, {
        pushToken,
        retryCount: REGISTER_PUSH_TOKEN_RETRY_LIMIT,
      })
    } catch (pushErr) {
      logger.error('Preload push registration failed', {}, pushErr)
    }
  }

  if (isIOS()) {
    const voipPushToken: string | null = yield call(BrownfieldBridgeInternal.getVoipPushToken)
    if (voipPushToken) {
      try {
        yield call(registerPushVoipToken, {
          pushVoipToken: voipPushToken,
          retryCount: REGISTER_PUSH_TOKEN_RETRY_LIMIT,
        })
      } catch (voipErr) {
        logger.error('Preload VoIP registration failed', {}, voipErr)
      }
    }
  }
}

// Preload sequence (embedded only):
//   1. Wait for JS initialization (store hydration + config) — takes(setInitialized) if needed
//   2. Emit PRELOAD_STARTED to native
//   3. featuresMonitor() — start LaunchDarkly flag polling
//   4. getExchangeToken() → login() with no UI/navigation side-effects
//   5. fetchUserInfo() and fetchPatientProfile() — load the profile required for push registration
//   6. Await FCM/APNS push registration (and VoIP registration on iOS)
//   7. Emit PRELOAD_SUCCESS { patientId } to native; dispatch preloadSuccess()
//   On any error: dispatch preloadFailure() and emit PRELOAD_FAILURE to native
//
// Native side dispatches this action only when token exchange is desired. When the host calls
// `start` with `skipTokenExchange = true` (typical VoIP / push-notification cold-start) or
// `preload = false`, the native layer boots RN without dispatching this action — preload is
// skipped entirely and the SDK relies on the persisted Dialogue auth token (paired with the
// `skipTokenExchange` initial view prop consumed by `App/Containers/App/index.tsx`).

/**
 * Preload saga for EMBEDDED mode.
 * Performs auth setup and data loading without UI/navigation initialization.
 *
 * @emits PRELOAD_STARTED - When preload begins
 * @emits PRELOAD_SUCCESS - When preload completes successfully
 * @emits PRELOAD_FAILURE - When preload fails
 */
export function* preload() {
  if (!isEmbeddedEnvironment()) {
    return
  }

  // Wait for JS context initialization if needed
  const { initialized } = yield select(
    (state: { initialization: { initialized: boolean } }) => state.initialization
  )
  if (!initialized) {
    yield take(initializationActions.setInitialized)
  }

  try {
    BrownfieldBridgeInternal.triggerEvent({ type: 'PRELOAD_STARTED' })

    yield call(featuresMonitor)

    // Get exchange token from native and perform token exchange login
    const exchangeToken: string = yield call(BrownfieldBridgeInternal.getExchangeToken)

    yield call(
      login,
      { triggerLogoutOnFailure: false, triggerStartupNav: false },
      { exchangeToken, type: LoginTypes.LOGIN_REQUEST_WITH_EXCHANGE_TOKEN }
    )

    const { accessToken }: { accessToken: string } = yield select(loginState)
    if (!accessToken) {
      const error = new Error('Exchange login did not produce an access token')
      logger.error('Exchange login did not produce an access token', {}, error)
      throw error
    }

    // fetchUserInfo never rethrows
    yield call(fetchUserInfo, accessToken)
    yield call(fetchPatientProfile)
    const profileForPush: { id?: number } | undefined = yield select(patientProfileState)
    if (!profileForPush?.id) {
      logger.warn('Preload: patient profile missing id')
    }

    // call (not put) to await registration without takeLatest double-dispatch.
    // retryCount === REGISTER_PUSH_TOKEN_RETRY_LIMIT is a single attempt (same as fullStartup).
    yield* registerPreloadPushTokens()

    // Mark preload as complete. fullStartup reads `preloaded` to know it
    // shouldn't take the standalone "already-set-up, just navigate" branch.
    yield put(preloadActions.preloadSuccess())

    BrownfieldBridgeInternal.triggerEvent({
      type: 'PRELOAD_SUCCESS',
      payload: { patientId: profileForPush?.id },
    })
  } catch (err: any) {
    yield put(preloadActions.preloadFailure(err))
    const failure = classifyStartupFailure(err)
    BrownfieldBridgeInternal.triggerEvent({
      type: 'PRELOAD_FAILURE',
      payload: {
        message: failure.message,
        code: failure.code,
        error: err?.toString(),
      },
    })
  }
}
